import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { AdminForms } from "src/containers/AdminForms";
import { useAdminDraftFormsQuery } from "src/hooks/admin/useAdminDraftForms";
import { useAdminPublishedFormsQuery } from "src/hooks/admin/useAdminPublishedForms";
import { Button } from "src/components/ui/button";
import { UserAdminFilter } from "src/containers/UserAdminFilter";

export type FormStatusTabPanelProps = {
	category: string;
};

const FormStatusTabPanel = ({ category }: FormStatusTabPanelProps) => {
	const [params] = useSearchParams();
	const status = params.get("status") || "";
	const tabValue = status === "draft" ? "draft" : "published";

	const {
		data: adminPublishedForms,
		isSuccess: isAdminPublishedFormsSuccess,
		isLoading: isAdminPublishedFormsLoading,
		isError: isAdminPublishedFormsError,
		error: AdminPublishedFormsError,
		refetch: refetchAdminPublishedForms,
		hasNextPage: hasPublishedFormsNextPage,
		isFetchingNextPage: isPublishedFormsFetchingNextPage,
		fetchNextPage: fetchPublishedFormsNextPage
	} = useAdminPublishedFormsQuery({ category, enabled: Boolean(category) });

	const {
		data: adminDraftForms,
		isSuccess: isAdminDraftFormsSuccess,
		isLoading: isAdminDraftFormsLoading,
		isError: isAdminDraftFormsError,
		error: AdminDraftFormsError,
		refetch: refetchAdminDraftForms,
		hasNextPage: hasDraftFormsNextPage,
		isFetchingNextPage: isDraftFormsFetchingNextPage,
		fetchNextPage: fetchDraftFormsNextPage
	} = useAdminDraftFormsQuery({ category, enabled: Boolean(category) });

	const published = adminPublishedForms
		? adminPublishedForms?.pages.flatMap(({ results }) => results)
		: [];

	const drafts = adminDraftForms
		? adminDraftForms?.pages.flatMap(({ results }) => results)
		: [];

	const categoryId = params.get("categoryId") || "";

	return (
		<div>
			<div className="flex items-center justify-between mb-10">
				<div
					className="flex w-full sm:w-auto bg-white border border-border p-1.5 rounded-xl shadow-xs"
					role="tablist"
				>
					<Link
						to={`/observations/forms/?status=published&category=${category}`}
						role="tab"
						aria-selected={tabValue === "published"}
						className={[
							"flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-center",
							tabValue === "published"
								? "bg-primary text-white shadow-md shadow-primary/50"
								: "text-muted-foreground hover:text-foreground hover:bg-white/80"
						].join(" ")}
					>
						Published ({adminPublishedForms?.pages[0].count ?? "..."})
					</Link>

					<Link
						to={`/observations/forms/?status=draft&category=${category}`}
						role="tab"
						aria-selected={tabValue === "draft"}
						className={[
							"flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-center",
							tabValue === "draft"
								? "bg-primary text-white shadow-md shadow-primary/50"
								: "text-muted-foreground hover:text-foreground hover:bg-white/80"
						].join(" ")}
					>
						Draft ({adminDraftForms?.pages[0].count ?? "..."})
					</Link>
				</div>

				<UserAdminFilter requiredPermission="CanViewEditObservationForms">
					<Link
						to={
							categoryId
								? `/observations/forms/new-observation-form?categoryId=${categoryId}`
								: "/observations/forms/new-observation-form"
						}
						className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary-dark transition-colors"
					>
						<Plus className="h-4 w-4" />
						New Form
					</Link>
				</UserAdminFilter>
			</div>

			{tabValue === "published" && (
				<div>
					<AdminForms
						showCardOptions={true}
						data={published}
						error={AdminPublishedFormsError}
						isError={isAdminPublishedFormsError}
						isLoading={isAdminPublishedFormsLoading}
						isSuccess={isAdminPublishedFormsSuccess}
						refetch={refetchAdminPublishedForms}
						status={status}
					/>

					{hasPublishedFormsNextPage && (
						<div className="flex items-center justify-center my-4">
							<Button
								onClick={() => fetchPublishedFormsNextPage()}
								disabled={isPublishedFormsFetchingNextPage}
							>
								{isPublishedFormsFetchingNextPage ? "Loading..." : "Load more"}
							</Button>
						</div>
					)}
				</div>
			)}

			{tabValue === "draft" && (
				<div>
					<AdminForms
						showCardOptions={true}
						data={drafts}
						error={AdminDraftFormsError}
						isError={isAdminDraftFormsError}
						isLoading={isAdminDraftFormsLoading}
						isSuccess={isAdminDraftFormsSuccess}
						refetch={refetchAdminDraftForms}
						status={status}
					/>

					{hasDraftFormsNextPage && (
						<div className="flex items-center justify-center my-4">
							<Button
								onClick={() => fetchDraftFormsNextPage()}
								disabled={isDraftFormsFetchingNextPage}
							>
								{isDraftFormsFetchingNextPage ? "Loading..." : "Load more"}
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
export default FormStatusTabPanel;
